import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Bell, Download, Eye, FileText, Phone, Receipt, TrendingUp, Users } from 'lucide-react';
import { TIME_RANGES, type PeriodDelta, type QuoteEngagement, type ReportSummary, type TimeRange } from '@worki/shared';
import { api, buildQuery } from '../lib/api.js';
import { formatDuration, money } from '../lib/format.js';
import { LoadingScreen, Select } from '../components/ui.js';
import { MiniChart } from '../components/MiniChart.js';
import { cn } from '../lib/cn.js';
import { currentLocale } from '../i18n.js';

/** One tinted domain card: an icon, a name and four figures. */
interface DomainCard {
  key: string;
  title: string;
  icon: typeof FileText;
  /** The card's one accent, applied to its wash, border, icon and headline figure. */
  tint: { gradient: string; border: string; iconBg: string; icon: string };
  stats: Array<{ label: string; value: string | number; highlight?: boolean }>;
}

const TINTS: Record<string, DomainCard['tint']> = {
  info: {
    gradient: 'from-info/20 to-info/5',
    border: 'border-info/30',
    iconBg: 'bg-info/15',
    icon: 'text-info',
  },
  primary: {
    gradient: 'from-primary/20 to-primary/5',
    border: 'border-primary/30',
    iconBg: 'bg-primary/15',
    icon: 'text-primary',
  },
  success: {
    gradient: 'from-success/20 to-success/5',
    border: 'border-success/30',
    iconBg: 'bg-success/15',
    icon: 'text-success',
  },
  warning: {
    gradient: 'from-warning/20 to-warning/5',
    border: 'border-warning/30',
    iconBg: 'bg-warning/15',
    icon: 'text-warning',
  },
  /* The housekeeping card. Every other tint on this screen is a claim about
     the number under it — green is good, amber wants attention. The tallies of
     calls, reminders and filed invoices make no such claim, and a neutral tint
     says so without spending one of the meaningful colours on them. */
  neutral: {
    gradient: 'from-muted-foreground/15 to-muted-foreground/5',
    border: 'border-muted-foreground/25',
    iconBg: 'bg-muted-foreground/10',
    icon: 'text-muted-foreground',
  },
};

/** REP-001..006 — reports & analytics. */
export function ReportsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [params] = useSearchParams();
  // DASH-006 — arriving from the dashboard's "view all reports" link carries
  // the range the user was already looking at, instead of resetting to month.
  const initialRange = params.get('range');
  const [range, setRange] = useState<TimeRange>(
    (TIME_RANGES as readonly string[]).includes(initialRange ?? '') ? (initialRange as TimeRange) : 'month',
  );

  const summary = useQuery({
    queryKey: ['reports', 'summary', range],
    queryFn: () => api.get<ReportSummary>(`/reports/summary${buildQuery({ range })}`),
  });

  const comparison = useQuery({
    queryKey: ['reports', 'comparison', range],
    queryFn: () => api.get<{ range: TimeRange; deltas: PeriodDelta[] | null }>(`/reports/comparison${buildQuery({ range })}`),
  });

  const engagement = useQuery({
    queryKey: ['reports', 'engagement', range],
    queryFn: () => api.get<QuoteEngagement>(`/reports/quotes/engagement${buildQuery({ range })}`),
  });

  const series = useQuery({
    queryKey: ['reports', 'series', range],
    queryFn: () =>
      api.get<{ range: TimeRange; points: Array<{ bucket: string; quotesCreated: number; quotesSent: number; quotesApproved: number; inquiriesCreated: number }> }>(
        `/reports/series${buildQuery({ range })}`,
      ),
  });

  const exportCsv = () => {
    void api.download(`/reports/quotes/export.csv${buildQuery({ range, tab: 'all' })}`, 'quotes.csv');
  };

  if (summary.isLoading || !summary.data) return <LoadingScreen />;
  const data = summary.data;
  const engaged = engagement.data;

  /*
   * The cards are built from the domains Worki actually measures. The
   * reference's "digital signing" card is left out rather than filled with
   * plausible numbers — nothing in this app signs a document, and a card of
   * invented signature rates is worse than a missing one.
   */
  const revenue = data.revenue;
  const growth =
    revenue.previousApprovedTotal === null || revenue.previousApprovedTotal === 0
      ? '—'
      : `${revenue.approvedTotal >= revenue.previousApprovedTotal ? '+' : ''}${Math.round(
          ((revenue.approvedTotal - revenue.previousApprovedTotal) / revenue.previousApprovedTotal) * 100,
        )}%`;

  const cards: DomainCard[] = [
    {
      key: 'quotes',
      title: t('reports.domains.quotes'),
      icon: FileText,
      tint: TINTS.info!,
      stats: [
        { label: t('dashboard.kpi.quotesSent'), value: data.quotes.sent },
        { label: t('dashboard.kpi.quotesApproved'), value: data.quotes.approved },
        { label: t('reports.quotesRejected'), value: data.quotes.rejected },
        { label: t('reports.conversionRate'), value: `${Math.round(data.quotes.conversionRate * 100)}%`, highlight: true },
      ],
    },
    {
      key: 'revenue',
      title: t('reports.domains.revenue'),
      icon: TrendingUp,
      tint: TINTS.success!,
      stats: [
        { label: t('reports.revenueApproved'), value: money(revenue.approvedTotal, revenue.currency, locale) },
        { label: t('reports.revenueAvgDeal'), value: money(revenue.avgDealSize, revenue.currency, locale) },
        { label: t('reports.revenueOpen'), value: money(revenue.openTotal, revenue.currency, locale) },
        { label: t('reports.revenueGrowth'), value: growth, highlight: true },
      ],
    },
    {
      key: 'inquiries',
      title: t('reports.domains.inquiries'),
      icon: Users,
      tint: TINTS.primary!,
      stats: [
        { label: t('reports.totalInquiries'), value: data.inquiries.total },
        // The enum labels describe one record ("a meeting was booked"); a count
        // of them needs the plural, so these are report labels of their own.
        { label: t('reports.inquiriesMeetings'), value: data.inquiries.byStatus.meeting_scheduled ?? 0 },
        { label: t('reports.inquiriesQuoteRequested'), value: data.inquiries.byStatus.quote_requested ?? 0 },
        {
          // What share of enquiries reached an outcome the owner acted on —
          // either ending counts, since asking for a price is as much a result
          // as booking a visit.
          label: t('reports.closeRate'),
          value: `${percent(
            (data.inquiries.byStatus.meeting_scheduled ?? 0) + (data.inquiries.byStatus.quote_requested ?? 0),
            data.inquiries.total,
          )}%`,
          highlight: true,
        },
      ],
    },
    {
      key: 'tracking',
      title: t('quotes.tracking'),
      icon: Eye,
      tint: TINTS.warning!,
      stats: [
        { label: t('reports.viewRate'), value: engaged ? `${Math.round(engaged.viewRate * 100)}%` : '—' },
        { label: t('reports.avgViewCount'), value: engaged ? engaged.avgViewCount.toFixed(1) : '—' },
        { label: t('reports.avgViewDuration'), value: engaged ? formatDuration(engaged.avgViewSeconds) : '—' },
        { label: t('reports.avgResponseTime'), value: engaged ? formatResponseTime(engaged.avgResponseHours, t) : '—', highlight: true },
      ],
    },
    {
      key: 'operations',
      title: t('reports.domains.operations'),
      icon: Phone,
      tint: TINTS.neutral!,
      stats: [
        { label: t('reports.domains.calls'), value: data.calls.total },
        { label: t('reports.domains.reminders'), value: data.reminders.total },
        { label: t('reports.domains.invoices'), value: data.invoices.total },
        { label: t('reports.openReminders'), value: data.reminders.byStatus.open ?? 0, highlight: true },
      ],
    },
  ];

  const points = series.data?.points ?? [];
  const chartEmpty = points.every(
    (p) => p.quotesCreated + p.quotesSent + p.quotesApproved + p.inquiriesCreated === 0,
  );

  return (
    <div className="animate-fade-up space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value={range}
          onChange={(value) => setRange(value as TimeRange)}
          aria-label={t('quotes.rangeLabel')}
          className="w-auto min-w-32 h-9 bg-muted font-medium"
        >
          {TIME_RANGES.map((option) => (
            <option key={option} value={option}>
              {t(`dashboard.range.${option}`)}
            </option>
          ))}
        </Select>
        {/* The reference puts a calendar next to the range picker; Worki has no
            calendar screen, and the thing owners ask this page for is the raw
            rows — so the accent button downloads them. */}
        <button
          type="button"
          onClick={exportCsv}
          className="h-9 px-3 rounded-lg bg-gradient-to-r from-primary/10 to-info/10 border border-primary/20 flex items-center gap-1.5 hover:from-primary/15 hover:to-info/15 transition-colors"
        >
          <Download className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium">{t('reports.exportCsv')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((card) => {
          const CardIcon = card.icon;
          return (
            <div
              key={card.key}
              className={cn('rounded-xl p-5 bg-gradient-to-br border', card.tint.gradient, card.tint.border)}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={cn('p-2.5 rounded-xl', card.tint.iconBg)}>
                  <CardIcon className={cn('h-5 w-5', card.tint.icon)} />
                </div>
                <h3 className="text-base font-bold">{card.title}</h3>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {card.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-card/80 backdrop-blur-sm rounded-lg p-3 text-center border border-border/30"
                  >
                    <p className={cn('text-lg font-bold ltr-nums', stat.highlight ? card.tint.icon : 'text-foreground')}>
                      {stat.value}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* REP-002 — trend chart. */}
      <section className="glass-card rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t('reports.chart')}</h3>
        </div>
        {series.isLoading ? (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        ) : chartEmpty ? (
          <p className="text-xs text-muted-foreground">{t('reports.noData')}</p>
        ) : (
          <MiniChart
            ariaLabel={t('reports.chart')}
            categories={points.map((p) => formatBucketLabel(p.bucket))}
            series={[
              { key: 'created', label: t('reports.quotesCreated'), color: 'hsl(var(--muted-foreground))', values: points.map((p) => p.quotesCreated) },
              { key: 'sent', label: t('reports.quotesSent'), color: 'hsl(var(--info))', values: points.map((p) => p.quotesSent) },
              { key: 'approved', label: t('reports.quotesApproved'), color: 'hsl(var(--success))', values: points.map((p) => p.quotesApproved) },
              { key: 'inquiries', label: t('reports.inquiriesCreated'), color: 'hsl(var(--primary))', values: points.map((p) => p.inquiriesCreated) },
            ]}
          />
        )}
      </section>

      {/* REP-004 — period comparison. Rows, not a table: on a phone a four-column
          table is unreadable, and each metric is a self-contained line anyway. */}
      <section className="glass-card rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t('reports.comparison')}</h3>
        </div>
        {!comparison.data?.deltas ? (
          <p className="text-xs text-muted-foreground">{t('reports.noComparison')}</p>
        ) : (
          <>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground px-1">
              <span>{t('reports.metric')}</span>
              <span className="flex items-center gap-3">
                <span className="w-14 text-center">{t(`dashboard.range.${range}`)}</span>
                <span className="w-14 text-center">{t('reports.vsLastPeriod')}</span>
                <span className="w-16 text-center">Δ</span>
              </span>
            </div>
            <div className="space-y-1.5">
              {comparison.data.deltas.map((delta) => (
                <div
                  key={delta.metric}
                  className="flex items-center justify-between gap-2 rounded-lg bg-muted-foreground/[0.06] px-3 py-2"
                >
                  <span className="text-xs font-medium min-w-0 truncate">
                    {t(`reports.${delta.metric}`, { defaultValue: delta.metric })}
                  </span>
                  <span className="flex items-center gap-3 shrink-0 ltr-nums">
                    <span className="w-14 text-center text-sm font-semibold">{delta.current}</span>
                    <span className="w-14 text-center text-xs text-muted-foreground">{delta.previous}</span>
                    <span
                      className={cn(
                        'w-16 text-center text-xs font-medium',
                        delta.deltaAbsolute >= 0 ? 'text-success' : 'text-destructive',
                      )}
                    >
                      {delta.deltaAbsolute >= 0 ? '+' : ''}
                      {delta.deltaAbsolute}
                      {delta.deltaPercent !== null &&
                        ` (${delta.deltaPercent >= 0 ? '+' : ''}${Math.round(delta.deltaPercent)}%)`}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <p className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground text-center">
        <Bell className="h-3 w-3" />
        {t('reports.reconcileNote')}
      </p>
    </div>
  );
}

/** A share of a whole, guarding the empty period that would divide by zero. */
function percent(part: number, whole: number): number {
  if (whole === 0) return 0;
  return Math.round((part / whole) * 100);
}

/**
 * The API reports one average in hours. A customer who approves two minutes
 * after the link arrives produced "0.0 hours", which reads as "no time at all"
 * and is indistinguishable from the no-data case — so the unit follows the
 * value: minutes below the hour, days above two.
 */
function formatResponseTime(hours: number | null, t: TFunction): string {
  if (hours === null) return '—';
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));
    return t('reports.responseMinutes', { count: minutes });
  }
  if (hours < 48) {
    const rounded = Number(hours.toFixed(1));
    return t('reports.responseHours', { count: rounded });
  }
  const days = Number((hours / 24).toFixed(1));
  return t('reports.responseDays', { count: days });
}

/**
 * Bucket strings arrive in three shapes depending on range (see
 * reportTimeSeries): `YYYY-MM-DDTHH:00` (today), `YYYY-MM-DD` (week/month),
 * `YYYY-MM` (all time). Each needs its own short axis label.
 */
function formatBucketLabel(bucket: string): string {
  if (bucket.includes('T')) return bucket.slice(11, 16); // HH:00
  if (bucket.length === 7) return bucket.slice(5); // MM
  return bucket.slice(5); // MM-DD
}
